import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Monitor, Tablet, Smartphone, Undo2, Redo2, Save, UploadCloud, RotateCcw, ExternalLink, Eye, EyeOff, Type, Move } from "lucide-react";
import { VEContext } from "../../context/VisualEditor";
import { Hero } from "../landing/Hero";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEVICE_W = { desktop: 1180, tablet: 820, mobile: 390 };
const PANEL_W = 720;
const ELEMENTS = [
  { id: "eyebrow", label: "Eyebrow badge" },
  { id: "headline", label: "Headline line 1" },
  { id: "headline2", label: "Headline line 2" },
  { id: "subtext", label: "Description" },
  { id: "book_btn", label: "Book Your Tour button" },
  { id: "offers_btn", label: "Special Offers button" },
  { id: "explore_btn", label: "Explore Tours button" },
  { id: "feature_rail", label: "Feature chips row" },
  { id: "video_player", label: "Hero video player" },
];
const FONTS = ["", "Inter, sans-serif", "Georgia, serif", "'Playfair Display', serif", "'Courier New', monospace", "system-ui, sans-serif"];
const WEIGHTS = ["", "300", "400", "500", "600", "700", "800", "900"];
const inputCls = "w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs focus:border-[var(--brand)] focus:outline-none";
const lbl = "text-[10px] font-bold uppercase tracking-wide text-slate-400";

export const VisualEditor = () => {
  const [draft, setDraftRaw] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const past = useRef([]);
  const future = useRef([]);
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/site-design/full`, { withCredentials: true });
    const sections = data.draft?.sections || data.sections || {};
    setDraftRaw({ sections });
  }, []);
  useEffect(() => { load(); }, [load]);

  const scheduleSave = useCallback((next) => {
    setDirty(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await axios.put(`${API}/site-design`, { sections: next.sections }, { withCredentials: true });
        setDirty(false);
      } catch { /* keep dirty */ }
    }, 700);
  }, []);

  const apply = useCallback((mutator, record = true) => {
    setDraftRaw((prev) => {
      if (!prev) return prev;
      const next = mutator(JSON.parse(JSON.stringify(prev)));
      if (record) { past.current.push(prev); future.current = []; }
      scheduleSave(next);
      return next;
    });
  }, [scheduleSave]);

  const updateEl = useCallback((section, id, patch) => {
    apply((d) => {
      const sec = d.sections[section] || (d.sections[section] = {});
      const ve = sec.ve || (sec.ve = {});
      ve[id] = { ...(ve[id] || {}), ...patch };
      return d;
    });
  }, [apply]);

  const sel = selectedId ? selectedId.split(":")[1] : null;
  const ov = (draft?.sections?.hero?.ve?.[sel]) || {};
  const devOffset = ov.offset?.[device] || { x: 0, y: 0 };
  const devFont = ov.fontSize?.[device] ?? "";
  const devScale = ov.scale?.[device] ?? 1;

  const setStyle = (patch) => sel && updateEl("hero", sel, patch);
  const setOffset = (x, y) => sel && updateEl("hero", sel, { offset: { ...(ov.offset || {}), [device]: { x, y } } });
  const setFont = (v) => sel && updateEl("hero", sel, { fontSize: { ...(ov.fontSize || {}), [device]: v === "" ? undefined : Number(v) } });
  const setScale = (v) => sel && updateEl("hero", sel, { scale: { ...(ov.scale || {}), [device]: v } });

  // arrow-key nudge
  useEffect(() => {
    const onKey = (e) => {
      if (!sel) return;
      if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
      if (["INPUT", "TEXTAREA"].includes(e.target.tagName) || e.target.isContentEditable) return;
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      let { x, y } = devOffset;
      if (e.key === "ArrowUp") y -= step;
      if (e.key === "ArrowDown") y += step;
      if (e.key === "ArrowLeft") x -= step;
      if (e.key === "ArrowRight") x += step;
      setOffset(x, y);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sel, device, devOffset]); // eslint-disable-line

  const undo = () => { if (!past.current.length) return; setDraftRaw((cur) => { future.current.push(cur); const p = past.current.pop(); scheduleSave(p); return p; }); };
  const redo = () => { if (!future.current.length) return; setDraftRaw((cur) => { past.current.push(cur); const n = future.current.pop(); scheduleSave(n); return n; }); };

  const publish = async () => {
    setBusy(true);
    try {
      await axios.put(`${API}/site-design`, { sections: draft.sections }, { withCredentials: true });
      await axios.post(`${API}/site-design/publish`, {}, { withCredentials: true });
      setDirty(false);
      toast.success("Published — your edits are now live on the website.");
    } catch { toast.error("Publish failed."); } finally { setBusy(false); }
  };

  const resetToPublished = async () => {
    if (!window.confirm("Discard draft edits and reload the published version?")) return;
    past.current = []; future.current = [];
    await load();
    toast.success("Reverted to the published version.");
  };

  const resetElement = () => {
    if (!sel) return;
    apply((d) => { if (d.sections.hero?.ve) delete d.sections.hero.ve[sel]; return d; });
  };

  const scale = Math.min(1, PANEL_W / DEVICE_W[device]);

  if (!draft) return <div className="p-10 text-center text-sm text-slate-400" data-testid="visual-editor">Loading the visual editor…</div>;

  return (
    <div data-testid="visual-editor">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-full p-1">
          {[{ id: "desktop", Icon: Monitor }, { id: "tablet", Icon: Tablet }, { id: "mobile", Icon: Smartphone }].map(({ id, Icon }) => (
            <button key={id} onClick={() => setDevice(id)} data-testid={`ve-device-${id}`} aria-label={id}
              className={`p-2 rounded-full transition-colors ${device === id ? "bg-[#0b132b] text-white" : "text-slate-400 hover:text-[#0b132b]"}`}><Icon className="w-4 h-4" /></button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={undo} data-testid="ve-undo" className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" aria-label="Undo"><Undo2 className="w-4 h-4" /></button>
          <button onClick={redo} data-testid="ve-redo" className="p-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50" aria-label="Redo"><Redo2 className="w-4 h-4" /></button>
          <button onClick={resetToPublished} data-testid="ve-reset" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-bold px-3 py-2 hover:bg-slate-50"><RotateCcw className="w-3.5 h-3.5" /> Reset</button>
          <a href="/" target="_blank" rel="noreferrer" data-testid="ve-preview" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-bold px-3 py-2 hover:bg-slate-50"><ExternalLink className="w-3.5 h-3.5" /> Preview</a>
          <button onClick={publish} disabled={busy} data-testid="ve-publish" className="inline-flex items-center gap-1.5 rounded-full bg-[var(--brand)] text-white text-xs font-bold px-5 py-2.5 shadow-lg shadow-[var(--brand)]/30 disabled:opacity-60"><UploadCloud className="w-3.5 h-3.5" /> Publish</button>
        </div>
      </div>
      <p className="text-xs mb-3" data-testid="ve-status">
        {dirty ? <span className="text-amber-600">Draft auto-saving…</span> : <span className="text-emerald-600">All changes saved to draft</span>}
        <span className="text-slate-400"> · Click an element to select, double-click text to type, drag to move, arrow keys to nudge.</span>
      </p>

      <div className="grid lg:grid-cols-[1fr_300px] gap-5">
        {/* Preview */}
        <div className="rounded-2xl border border-slate-200 bg-slate-100 p-4 overflow-auto">
          <div className="mx-auto bg-white shadow-xl rounded-lg overflow-hidden" style={{ width: DEVICE_W[device] * scale }}>
            <div style={{ width: DEVICE_W[device], transform: `scale(${scale})`, transformOrigin: "top left", height: 620 * (1 / scale) }} onClick={() => setSelectedId(null)}>
              <VEContext.Provider value={{ editMode: true, device, scale, overrides: draft.sections, selectedId, select: setSelectedId, updateEl }}>
                <Hero />
              </VEContext.Provider>
            </div>
          </div>
        </div>

        {/* Inspector */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="ve-inspector">
          {!sel ? (
            <div className="text-center text-xs text-slate-400 py-8">
              <Move className="w-6 h-6 mx-auto mb-2 text-slate-300" />
              Select an element on the preview to edit its text, style and position.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-[#0b132b]">{ELEMENTS.find((e) => e.id === sel)?.label || sel}</span>
                <button onClick={() => updateEl("hero", sel, { hidden: !ov.hidden })} data-testid="ve-el-hide" className="text-slate-400 hover:text-[var(--brand)]" aria-label="Toggle visibility">
                  {ov.hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <label className="block"><span className={lbl}>Text</span>
                <textarea rows={2} value={ov.text ?? ""} onChange={(e) => setStyle({ text: e.target.value })} placeholder="(default text)" className={`${inputCls} mt-1`} data-testid="ve-el-text" /></label>

              <div className="grid grid-cols-2 gap-2">
                <label className="block"><span className={lbl}>Text colour</span>
                  <input type="color" value={ov.color || "#0b132b"} onChange={(e) => setStyle({ color: e.target.value })} className="mt-1 w-full h-8 rounded border border-slate-200" data-testid="ve-el-color" /></label>
                <label className="block"><span className={lbl}>Background</span>
                  <input type="color" value={ov.backgroundColor || "#ffffff"} onChange={(e) => setStyle({ backgroundColor: e.target.value })} className="mt-1 w-full h-8 rounded border border-slate-200" data-testid="ve-el-bg" /></label>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <label className="block"><span className={lbl}>Font size · {device} (px)</span>
                  <input type="number" value={devFont} onChange={(e) => setFont(e.target.value)} placeholder="auto" className={`${inputCls} mt-1`} data-testid="ve-el-fontsize" /></label>
                <label className="block"><span className={lbl}>Weight</span>
                  <select value={ov.fontWeight || ""} onChange={(e) => setStyle({ fontWeight: e.target.value || undefined })} className={`${inputCls} mt-1`} data-testid="ve-el-weight">
                    {WEIGHTS.map((w) => <option key={w} value={w}>{w || "default"}</option>)}
                  </select></label>
              </div>

              <label className="block"><span className={lbl}>Font family</span>
                <select value={ov.fontFamily || ""} onChange={(e) => setStyle({ fontFamily: e.target.value || undefined })} className={`${inputCls} mt-1`} data-testid="ve-el-font">
                  {FONTS.map((f) => <option key={f} value={f}>{f ? f.split(",")[0].replace(/'/g, "") : "default"}</option>)}
                </select></label>

              <div className="grid grid-cols-2 gap-2">
                <label className="block"><span className={lbl}>Alignment</span>
                  <select value={ov.textAlign || ""} onChange={(e) => setStyle({ textAlign: e.target.value || undefined })} className={`${inputCls} mt-1`} data-testid="ve-el-align">
                    {["", "left", "center", "right"].map((a) => <option key={a} value={a}>{a || "default"}</option>)}
                  </select></label>
                <label className="block"><span className={lbl}>Line height</span>
                  <input type="number" step="0.1" value={ov.lineHeight || ""} onChange={(e) => setStyle({ lineHeight: e.target.value || undefined })} placeholder="auto" className={`${inputCls} mt-1`} data-testid="ve-el-lineheight" /></label>
              </div>

              <div>
                <span className={lbl}>Size · {device} ({Math.round(devScale * 100)}%)</span>
                <div className="mt-1 flex items-center gap-2">
                  <input type="range" min={0.3} max={2.5} step={0.05} value={devScale} onChange={(e) => setScale(Number(e.target.value))} className="flex-1 accent-[var(--brand)]" data-testid="ve-el-scale" />
                  <button onClick={() => setScale(1)} className="text-[10px] font-bold text-slate-400 hover:text-[var(--brand)]" data-testid="ve-el-scale-reset">100%</button>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Make this element smaller or larger — saved per device.</p>
              </div>

              <div>
                <span className={lbl}>Position · {device} (px)</span>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-1"><span className="text-[10px] text-slate-400">X</span>
                    <input type="number" value={devOffset.x || 0} onChange={(e) => setOffset(Number(e.target.value), devOffset.y || 0)} className={inputCls} data-testid="ve-el-x" /></div>
                  <div className="flex items-center gap-1"><span className="text-[10px] text-slate-400">Y</span>
                    <input type="number" value={devOffset.y || 0} onChange={(e) => setOffset(devOffset.x || 0, Number(e.target.value))} className={inputCls} data-testid="ve-el-y" /></div>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Positions are saved per device — moving on mobile won't change desktop.</p>
              </div>

              <button onClick={resetElement} data-testid="ve-el-reset" className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 text-slate-500 text-xs font-bold px-3 py-2 hover:bg-slate-50"><RotateCcw className="w-3.5 h-3.5" /> Reset this element</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
